import {
  Box,
  Chip,
  FormControlLabel,
  FormGroup,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  Typography,
  withStyles,
} from "@material-ui/core";
import React, { ChangeEvent, useEffect, useState } from "react";
import Page from "src/components/Page";
import { withSchoolContext } from "src/pages/DirectorPage";
import { ICoach, ISport } from "src/services";
import { SportCheckboxes } from "./components/SportCheckboxes/SportCheckboxes";
import { SurveyCharts } from "./components/SurveyCharts/SurveyCharts";
import {
  DATA_CENTER_HEADER,
  DATA_CENTER_SUBTITLE,
  DEFAULT_COACH_ID,
  ERROR_MESSAGE,
  NO_SELECT,
  ONLY_SHOW_INACTIVE_LABEL,
  SELECT_LABEL,
  SELECT_LABEL_AD,
  SHOW_INACTIVE_LABEL,
  TOGGLE_LABEL,
  TOGGLE_LABEL_AD
} from "./constants";
import { IFormattedSurveyEvaluations } from "./hooks/types";
import { useSurveyEvaluationsHistory } from "./hooks/useSurveyEvaluationsHistory";
import { styles } from "./styles";
import { ISurveyDataCenterPageProps } from "./types";
import { getHeaderText } from "./utils/getHeaderText/getHeaderText";

const SurveyDataCenterPage = ({
  classes,
  coaches,
  school,
  surveys,
  loadSurveys,
}: ISurveyDataCenterPageProps) => {
  const [showInactive, toggleShowInactive] = useState(false);
  const [onlyShowInactive, toggleOnlyShowInactive] = useState(false);
  const [currentCoachId, setCurrentCoachId] = useState(DEFAULT_COACH_ID);
  const [currentCoach, setCurrentCoach] = useState({} as ICoach);
  const [viewedCoaches, setViewedCoaches] = useState<ICoach[]>([]);
  const [includedSports, setIncludedSports] = useState<ISport[]>([]);
  const [filteredChartPoints, setFilteredChartPoints] =
    useState<IFormattedSurveyEvaluations | null>(null);

  const { formattedSurveyEvaluations, hasError, isLoading } =
    useSurveyEvaluationsHistory({
      currentCoachId,
      loadSurveys,
      schoolSlug: school.slug,
      surveys,
    });

  // Add the flag to check if the slug contains "ADSurveys"
  const isADSurveys = school.slug.includes("ADSurveys");

  console.log("Is ADSurveys:", isADSurveys); // Optional: For debugging

  const includedSportIds = includedSports.map((sport) => sport.id);
  const isCoachSelected = currentCoachId !== DEFAULT_COACH_ID;
  const currentCoachName =
    coaches.find((coach) => coach.id === currentCoachId)?.name || "";
  const hasEvaluations = Boolean(formattedSurveyEvaluations);
  const HEADER_TEXT = getHeaderText({
    currentCoachName,
    hasEvaluations,
    isCoachSelected,
    isADSurveys
  });

  const handleSelectChange = (e: ChangeEvent) => {
    const element = e.target as HTMLSelectElement;
    setCurrentCoachId(parseInt(element.value, 10));
  };

  const handleToggleInactive = () => {
    toggleShowInactive((prevShowInactive) => {
      const newShowInactive = !prevShowInactive;

      if (!newShowInactive) {
        toggleOnlyShowInactive(false);
      }
      return newShowInactive;
    });
  };

  const handleToggleOnlyInactive = () => {
    toggleOnlyShowInactive((prevOnlyShowInactive) => {
      const newOnlyShowInactive = !prevOnlyShowInactive;

      if (newOnlyShowInactive) {
        toggleShowInactive(true);
      }
      return newOnlyShowInactive;
    });
  };

  const filterCoaches = (coach: ICoach) => {
    if (coach.active) {
      return !onlyShowInactive;
    } else {
      return showInactive;
    }
  };

  useEffect(() => {
    const filteredCoaches = coaches.filter(filterCoaches);
    const filteredCoachesById = filteredCoaches.map((coach) => coach.id);

    if (!filteredCoachesById.includes(currentCoachId)) {
      setCurrentCoachId(DEFAULT_COACH_ID);
    }
    setViewedCoaches(filteredCoaches);
  }, [showInactive, onlyShowInactive]);

  useEffect(() => {
    if (isCoachSelected) {
      const newCurrentCoach = (coaches.find(
        (coach) => coach.id === currentCoachId
      ) || {}) as ICoach;
      setCurrentCoach(newCurrentCoach);
      setIncludedSports(newCurrentCoach?.sports ?? []);
    } else {
      setIncludedSports([]);
    }
  }, [currentCoachId]);

  useEffect(() => {
    const filterChartPoints = (category: keyof IFormattedSurveyEvaluations) => {
      return (
        formattedSurveyEvaluations?.[category]?.filter((chartPoint) =>
          includedSportIds.includes(chartPoint.sportId)
        ) ?? []
      );
    };

    setFilteredChartPoints({
      expectations: filterChartPoints("expectations"),
      overall: filterChartPoints("overall"),
      warmth: filterChartPoints("warmth"),
    });
  }, [formattedSurveyEvaluations, includedSports]);
  
  useEffect(() => {
    document.body.classList.add("surveydatacenter");
    return () => {
      document.body.classList.remove("surveydatacenter");
    };
  }, []);

  return (
    <Page header={DATA_CENTER_HEADER} subtitle={DATA_CENTER_SUBTITLE}>
      <Box
        className={classes.formsWrapper}
        display="flex"
        flexDirection="row"
        flexWrap="wrap"
        gridGap="2rem"
      >
        <Box>
          <InputLabel id="label" className={classes.inputLabel}>
            {isADSurveys ? SELECT_LABEL_AD : SELECT_LABEL}
          </InputLabel>
          <Select
            labelId="label"
            id="select"
            value={currentCoachId}
            onChange={handleSelectChange as any}
          >
            <MenuItem value={DEFAULT_COACH_ID}>{NO_SELECT}</MenuItem>
            {viewedCoaches.map((coach) => {
              const { id, name } = coach;
              return (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              );
            })}
          </Select>
        </Box>
        <Box>
          <InputLabel className={classes.inputLabel}>
          {isADSurveys ? TOGGLE_LABEL_AD : TOGGLE_LABEL}
         </InputLabel>
          <FormGroup row={true}>
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  value="check"
                  onChange={handleToggleInactive}
                />
              }
              label={
                <Typography variant="body2">{SHOW_INACTIVE_LABEL}</Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={onlyShowInactive}
                  value="check"
                  onChange={handleToggleOnlyInactive}
                />
              }
              label={
                <Typography variant="body2">
                  {ONLY_SHOW_INACTIVE_LABEL}
                </Typography>
              }
            />
          </FormGroup>
        </Box>
      </Box>
      <Box>
        {isLoading && <LinearProgress />}
        {!isLoading && (
          <Box className={classes.chartsWrapper}>
            {hasError && <Typography variant="h5">{ERROR_MESSAGE}</Typography>}
            {!hasError && (
              <>
                <Typography variant="h5" className={classes.chartsHeader}>
                  {HEADER_TEXT}
                  {hasEvaluations && (
                    <Box className={classes.chipWrapper}>
                      {currentCoach.sports?.map((sport, index) => (
                        <Chip
                          key={sport.id}
                          label={sport.name}
                          size="small"
                          style={{ marginLeft: index !== 0 ? "0.5rem" : 0 }}
                        />
                      ))}
                    </Box>
                  )}
                </Typography>
                {formattedSurveyEvaluations && (
                  <>
                    <SportCheckboxes
                      currentCoach={currentCoach}
                      includedSports={includedSports}
                      setIncludedSports={setIncludedSports}
                    />
                    <SurveyCharts chartPoints={filteredChartPoints} />
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Page>
  );
};

export default withSchoolContext(withStyles(styles)(SurveyDataCenterPage));



// WEBPACK FOOTER //
// ./src/pages/director/SurveyDataCenter/SurveyDataCenterPage.tsx